import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { BackendAPI } from '../../lib/api';

interface Pack {
  id: string;
  credits: number;
  amount_cents: number;
  label: string;
}

interface Props {
  /** Where to send the browser for Stripe Checkout. Injected for testability. */
  redirect?: (url: string) => void;
}

/**
 * Pack picker that starts Stripe Checkout. Prices are fetched from the backend
 * (`/billing/packs`) so the displayed price always matches what Stripe charges —
 * no hardcoded amounts that could drift from `app/services/billing/packs.py`.
 */
export default function BuyCreditsPanel({
  redirect = (url) => window.location.assign(url),
}: Props) {
  const [packs, setPacks] = useState<Pack[] | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    BackendAPI.getBillingPacks()
      .then((r) => {
        if (cancelled) return;
        setPacks(r.packs);
        setEnabled(r.enabled);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load pricing. Try again, or email support.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function buy(packId: string) {
    setBusyId(packId);
    setError(null);
    try {
      const { url } = await BackendAPI.createCheckoutSession(packId);
      redirect(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout.');
      setBusyId(null);
    }
  }

  if (!packs) {
    return <p className="text-[13px] text-[var(--tx-lo)]">{error ?? 'Loading pricing…'}</p>;
  }

  if (!enabled) {
    return (
      <p className="text-[12.5px] text-[var(--tx-faint)]">
        Payments turn on shortly — check back soon.
      </p>
    );
  }

  return (
    <div className="grid gap-2">
      {packs.map((p) => {
        const dollars = Math.round(p.amount_cents / 100);
        const noun = p.credits === 1 ? 'credit' : 'credits';
        return (
          <Button
            key={p.id}
            variant="primary"
            className="w-full justify-center"
            disabled={busyId !== null}
            onClick={() => buy(p.id)}
          >
            {busyId === p.id ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Starting checkout…
              </span>
            ) : (
              `${p.credits} ${noun} — $${dollars}`
            )}
          </Button>
        );
      })}
      {error && <p className="text-[12.5px] font-medium text-[var(--bad)]">{error}</p>}
    </div>
  );
}
